import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, RotateCcw, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { fmtDate } from "@/lib/format";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

function TrashRow({ t, onRestore, onSummary }) {
  const d = daysLeft(t.purge_after);
  return (
    <div className="lux-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`trash-row-${t.ref}`}>
      <div className="flex-1 min-w-0">
        <p className="font-mono-data text-sm text-white break-words">{t.ref}</p>
        <p className="text-xs text-zinc-500 mt-1">
          {t.broker_name} · deleted {fmtDate(t.deleted_at)}{t.deleted_by_name ? ` by ${t.deleted_by_name}` : ""}
        </p>
        <p className="text-[11px] text-rose-500 mt-1" data-testid={`trash-row-${t.ref}-days-left`}>
          {d === 0 ? "Erased at the next daily clean-up" : `Erased in ${d} day${d === 1 ? "" : "s"}`}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        <button className="frame-btn" onClick={() => onSummary(t)} data-testid={`trash-row-${t.ref}-summary-btn`}>
          <FileDown size={13} /> Trip summary
        </button>
        {onRestore && (
          <button className="frame-btn gold" onClick={() => onRestore(t)} data-testid={`trash-row-${t.ref}-restore-btn`}>
            <RotateCcw size={13} /> Restore
          </button>
        )}
      </div>
    </div>
  );
}

export default function TrashBin({ count, onChanged }) {
  const api = useApi();
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/trash").then((r) => setItems(r.data)).catch(() => setItems([])), [api]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const restore = async (t) => {
    try {
      await api.post(`/trash/${t.id}/restore`);
      toast.success(`${t.ref} restored`);
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const summary = (t) => downloadBlob(api, `/trash/${t.id}/summary.pdf`, `Trip-Summary-${t.ref.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`)
    .catch(() => toast.error("Couldn't download the trip summary"));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="frame-btn !py-2.5 shrink-0" data-testid="trash-open-btn"><Trash2 size={13} /> Trash ({count})</button>
      </DialogTrigger>
      <DialogContent className="bg-[#111319] border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="trash-dialog">
        <DialogHeader>
          <p className="eyebrow">Trash bin</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">Deleted trips</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-400 -mt-1">Deleted trips stay here for 7 days, then they're erased with their options, photos, chat and client links. Download the trip summary PDF to keep a record.</p>
        <div className="space-y-3 mt-2" data-testid="trash-list">
          {items?.map((t) => <TrashRow key={t.id} t={t} onRestore={me?.role === "admin" ? restore : null} onSummary={summary} />)}
          {items?.length === 0 && <p className="text-sm text-zinc-500" data-testid="trash-empty">The trash is empty.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
